import React, { FC, useMemo } from 'react';
import withPDF from '../../hoc/withPDF';
import useCoverSheetOverview from '../../../hooks/useCoverSheetOverview';

import AnomalyRecommendation from './anomaly-recommendation';
import Conditional from '../../common/conditional';
import CustomUTMReportPDF from '../custom-utm/custom-utm-report';
import CustomGeneralPhotos from '../custom-general/custom-general-photos';
import CustomPDFExport from '../../blocks/pdf-export';
import CustomInfoSheet from '../custom-general/custom-info-sheet';
import CustomCoverPage from '../custom-general/custom-cover-page';
import DamageClassSummary from './damage-class-summary';
import ExecutiveSummary from './executive-summary';
import OverviewSheet from '../custom-general/overview-sheet';
import TankAnalytics from './report-items/tankAnalytics';

import * as models from '../../../utils/models';
import * as utils from '../../../utils/utilities';

interface FVITankReportProps {
	additionalComment: string;
	anomalyLocationData: models.AnomalyLocationData;
	coverData: models.CustomFVIData;
	executiveSummary: string;
	includeSummary: models.IncludeSummary;
	projectDetails: models.Project;
	pidData: models.PIDData;
	reportTitle: string;
	structureItems: models.FVIStructureItem[];
	checklistItems: models.ChecklistItem[];
	generalPhotos: models.PDFImageProps[];
	tmlLocations: models.PDFImageProps[];
	utInspectionItems: models.UTMInspection[];
	inspectionOverview: models.CustomUTMOverview;
}

const FVITankReport: FC<FVITankReportProps> = (props) => {
	const { inspectionTypeName } = props.projectDetails;

	const referenceDocuments: models.CoverDataItem[] = [
		{
			label: '1. Scope Of Work',
			value: props.coverData?.scopeOfWork,
		},
		{
			label: '2. VT Procedure.',
			value: props.coverData?.vtProcedure,
		},
		{
			label: '3. Rope Access Code of Practice.',
			value: props.coverData?.ropeAccessCodeOfPractice,
		},
		{
			label: '4. Work Procedure (Structure).',
			value: props.coverData?.exxonMobilWorkProcedure,
		},
	];

	const standardCriteriaItems: models.CoverDataItem[] = useMemo(() => {
		return props.coverData.coverData.map((item, index) => ({
			label: `${index + 1}. ${item.id}`,
			value: item.name.toString(),
		}));
	}, [props.coverData.coverData]);

	const executiveSummary = useMemo(() => {
		if (props.includeSummary === 'include') {
			return props.executiveSummary + utils.getExecutiveSummaryAffix(props.structureItems, 'tank sections(s)');
		}

		return props.executiveSummary;
	}, [props.structureItems, props.includeSummary, props.executiveSummary]);

	const overview = useCoverSheetOverview();
	const inspectionTypeLabel = utils.getExtractedInspectionName(inspectionTypeName);

	return (
		<React.Fragment>
			<CustomCoverPage reportTitle={inspectionTypeLabel} />

			<CustomInfoSheet
				projectDetails={props.projectDetails}
				workOrderNumber={props.coverData.workOrderNumber}
				reportTitle={props.reportTitle}
			/>

			<OverviewSheet
				overviewData={overview}
				referenceDocuments={referenceDocuments}
				standardCriteriaItems={standardCriteriaItems}
				projectDetails={props.projectDetails}
			/>

			<ExecutiveSummary summary={executiveSummary} />

			<DamageClassSummary
				items={props.structureItems} 
				itemNumberKey='structureMemberName' 
				columns={damageClassColumns}
			/>

			<CustomPDFExport>
				{(params) => (
					<AnomalyRecommendation
						projectDetails={props.projectDetails} 
						items={props.structureItems}
						exportParams={params}
						title='Summary'
						showSummarySection={false}
					/>
				)}
			</CustomPDFExport>

			<TankAnalytics 
				anomalyLocationData={props.anomalyLocationData}
				additionalComment={props.additionalComment} 
				checklistItems={props.checklistItems} 
				items={props.structureItems} 
				pidData={props.pidData}
			/>

			<CustomUTMReportPDF
				tmlLocations={props.tmlLocations}
				utInspectionItems={props.utInspectionItems}
				inspectionOverview={props.inspectionOverview}
			/>

			<Conditional isVisible={props.generalPhotos && props.generalPhotos.length > 0}>
				<CustomGeneralPhotos label="General Photos" photos={props.generalPhotos} />
			</Conditional>
		</React.Fragment>
	);
};

const damageClassColumns: models.Column[] = [
	{
		path: 'structureMemberName',
		label: 'Structure Member Name',
	},
];

export default withPDF(FVITankReport);
